const ALLOWED_TAGS = new Set([
  'P','DIV','BR','B','STRONG','I','EM','U','S','UL','OL','LI','BLOCKQUOTE','SPAN','FONT'
]);

const ALLOWED_STYLE_PROPS = new Set([
  'font-family','font-size','font-weight','font-style','text-decoration','text-align','margin-left'
]);

function cleanStyle(styleText = '') {
  return styleText
    .split(';')
    .map(part => part.trim())
    .filter(Boolean)
    .map(part => {
      const idx = part.indexOf(':');
      if (idx === -1) return '';
      const prop = part.slice(0, idx).trim().toLowerCase();
      let value = part.slice(idx + 1).trim();
      if (!ALLOWED_STYLE_PROPS.has(prop)) return '';
      if (/url\s*\(|expression\s*\(|javascript:/i.test(value)) return '';

      if (prop === 'font-size') {
        const match = value.match(/^(\d+(?:\.\d+)?)px$/i);
        if (!match) return '';
        const px = Math.min(72, Math.max(8, Number(match[1])));
        value = `${px}px`;
      }
      if (prop === 'font-family' && !/^[a-z0-9 ,"'-]{1,80}$/i.test(value)) return '';
      if (prop === 'text-align' && !/^(left|center|right|justify)$/i.test(value)) return '';
      if (prop === 'margin-left' && !/^\d+(?:\.\d+)?(px|em|rem)$/i.test(value)) return '';
      if (prop === 'font-weight' && !/^(normal|bold|[1-9]00)$/i.test(value)) return '';
      if (prop === 'font-style' && !/^(normal|italic|oblique)$/i.test(value)) return '';
      if (prop === 'text-decoration' && !/^(none|underline|line-through)(\s+(underline|line-through))?$/i.test(value)) return '';

      return `${prop}: ${value}`;
    })
    .filter(Boolean)
    .join('; ');
}

export function sanitizeRichHtml(html = '') {
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') return String(html || '');
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return '';

  const cleanNode = (node) => {
    Array.from(node.childNodes).forEach(child => {
      if (child.nodeType === 8) {
        child.remove();
        return;
      }
      if (child.nodeType !== 1) return;

      if (!ALLOWED_TAGS.has(child.tagName)) {
        const fragment = doc.createDocumentFragment();
        while (child.firstChild) fragment.appendChild(child.firstChild);
        child.replaceWith(fragment);
        // Re-run the parent so elements unwrapped from this node are sanitized too.
        cleanNode(node);
        return;
      }

      Array.from(child.attributes).forEach(attr => {
        const name = attr.name.toLowerCase();
        if (name === 'style') {
          const cleaned = cleanStyle(attr.value);
          if (cleaned) child.setAttribute('style', cleaned);
          else child.removeAttribute('style');
        } else if (child.tagName === 'FONT' && name === 'face') {
          if (!/^[a-z0-9 ,"'-]{1,80}$/i.test(attr.value)) child.removeAttribute(attr.name);
        } else if (child.tagName === 'FONT' && name === 'size') {
          if (!/^[1-7]$/.test(attr.value)) child.removeAttribute(attr.name);
        } else {
          child.removeAttribute(attr.name);
        }
      });
      cleanNode(child);
    });
  };

  cleanNode(root);
  return root.innerHTML;
}

export function htmlToPlainText(html = '') {
  if (!html) return '';
  if (typeof document === 'undefined') return String(html).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const el = document.createElement('div');
  el.innerHTML = sanitizeRichHtml(html);
  return (el.innerText || el.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
}

export function plainTextToHtml(text = '') {
  const escaped = String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped.split(/\n/).map(line => `<div>${line || '<br>'}</div>`).join('');
}